import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  Avatar,
  Chip,
  CircularProgress,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Assistant as AssistantIcon,
} from '@mui/icons-material';
import { chatWithLLM, getSuggestedNextSteps } from '../services/api';

/**
 * Investigation Assistant - AI Chat Interface
 * Floating assistant that analysts can ask questions
 *
 * Usage:
 * <InvestigationAssistant
 *   userId="analyst_001"
 *   sessionId="session_abc"
 *   investigationContext={{
 *     phone_numbers: ['243970005050'],
 *     countries: ['Rwanda'],
 *     threat_level: 'high'
 *   }}
 * />
 */
export default function InvestigationAssistant({
  userId,
  sessionId,
  investigationContext = {},
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your AI intelligence assistant. I can help analyze threats, suggest next steps, or answer questions about your investigation. What would you like to know?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && Object.keys(investigationContext).length > 0) {
      loadSuggestions();
    }
  }, [open, investigationContext]);

  const loadSuggestions = async () => {
    try {
      const result = await getSuggestedNextSteps(investigationContext, userId);
      if (result.success && result.suggestions) {
        setSuggestions(result.suggestions);
      }
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await chatWithLLM(
        input,
        investigationContext,
        userId,
        sessionId
      );

      const assistantMessage = {
        role: 'assistant',
        content: response.answer,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage = {
        role: 'assistant',
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
        error: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion.action);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
        onClick={() => setOpen(true)}
      >
        <AssistantIcon />
      </Fab>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 450 } },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <AppBar position="static" elevation={0}>
            <Toolbar>
              <PsychologyIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                AI Intelligence Assistant
              </Typography>
              <IconButton color="inherit" onClick={() => setOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Suggested Actions:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {suggestions.slice(0, 3).map((suggestion, idx) => (
                  <Chip
                    key={idx}
                    label={suggestion.action.substring(0, 40) + '...'}
                    size="small"
                    clickable
                    onClick={() => handleSuggestionClick(suggestion)}
                    color={
                      suggestion.priority === 'HIGH' || suggestion.priority === 'CRITICAL'
                        ? 'error'
                        : 'default'
                    }
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider />

          {/* Messages */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: 'background.default',
            }}
          >
            <List>
              {messages.map((message, idx) => (
                <ListItem
                  key={idx}
                  sx={{
                    flexDirection: 'column',
                    alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      maxWidth: '85%',
                      flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                        width: 32,
                        height: 32,
                      }}
                    >
                      {message.role === 'user' ? <PersonIcon /> : <PsychologyIcon />}
                    </Avatar>

                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                        color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          opacity: 0.7,
                        }}
                      >
                        {message.timestamp.toLocaleTimeString()}
                      </Typography>
                    </Paper>
                  </Box>
                </ListItem>
              ))}

              {loading && (
                <ListItem sx={{ justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                    AI is thinking...
                  </Typography>
                </ListItem>
              )}

              <div ref={messagesEndRef} />
            </List>
          </Box>

          {/* Input */}
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Ask about threats, patterns, or next steps..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                size="small"
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!input.trim() || loading}
              >
                <SendIcon />
              </IconButton>
            </Box>

            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              💡 Try: "What should I investigate next?" or "Is this Rwanda connection suspicious?"
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
